import { useMemo } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, useTheme, Divider } from "@mui/material";
import {
    Done as DoneIcon,
    Report as ReportIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
    Info as InfoIcon
} from "@mui/icons-material";
import { pink } from "@mui/material/colors";
import { muiGreen, muiAmber, prodBase } from "@initia-digital/colors";
import { DateTime } from "luxon";

import { isAuthenticatedMSAL, useMSALPayload } from "@initia-digital/auth-client-utils";
import {
    CriticalOrSiteSpecificHazardListItemReadOnly, GeneralChip,
    IconButton, MSFileTypeIcon, PlanRegisterHazardClassListItemReadOnly,
    Typography, useSnackbar
} from "@initia-digital/component-library";
import { baseDefinedAndNotNullNarrowing, pluralize, prepNullableFieldToString, formatDisplayString } from "@initia-digital/ts-utils";
import { compareHazardsByRiskLevel } from "@initia-digital/data-service-mapping";
import { IEquipmentClassUse } from "@initia-digital/types/work-and-safety";

import { useWorkAndSafetyStore } from "../store";

// Standard filenames for service clearance uploads
const STANDARD_FILENAMES = {
    GROUND_CLEARANCE_CHECKLIST: "Ground Clearance Checklist",
    INVESTIGATION_PLAN: "Site Investigation Plan"
};
import { getS3Object } from "../api/objectStorage";
import { sortHazardClassesByPresentationOrder, sortPlanRegisterHazardClassPresentationOrder } from "../utils";
import { useHazardClassList } from "../hooks";


export const EditPlanSummary = () => {
    // --------------------------------

    const { editPlanState, updateEditPlanState } = useWorkAndSafetyStore();
    const {
        objectives,
        description, supportingContext,
        startDate, endDate,
        teamMembers,
        serviceClearanceRequired,
        testsRequired, testSchedule,
        installationsRequired, installationSchedule,
        bookings, equipment,
        fileUploads,
        criticalHazards, hazardsFromRegister
    } = editPlanState;
    const shouldCalculateDuration = baseDefinedAndNotNullNarrowing(startDate) && baseDefinedAndNotNullNarrowing(endDate);
    const duration = shouldCalculateDuration ? DateTime.fromISO(endDate).endOf('day').diff(DateTime.fromISO(startDate).startOf('day'), 'days', { conversionAccuracy: 'longterm' }) : null;

    // Check for uploaded files with standard names
    const hasGroundClearanceChecklist = fileUploads.some(file =>
        file.name.includes(STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST) && file.includeInAppendix
    );
    const hasInvestigationPlan = fileUploads.some(file =>
        file.name.includes(STANDARD_FILENAMES.INVESTIGATION_PLAN) && file.includeInAppendix
    );
    const serviceClearanceBooked = bookings.rows.filter(booking => booking.bookingType === 'Service clearance').length > 0;

    const authDetail = useMSALPayload();
    const isAuthenticated = isAuthenticatedMSAL(authDetail);

    const theme = useTheme();
    const [searchParams] = useSearchParams();

    const navigate = useNavigate();

    const { queueSnackbar } = useSnackbar();

    const { hazardClassListData } = useHazardClassList(authDetail, true, { status: 'ACTIVE' });
    const hazardClassListAreDefined = baseDefinedAndNotNullNarrowing(hazardClassListData);

    const referenceHazardClassPresentationOrder = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return [];
        return [...hazardClassListData].sort(sortHazardClassesByPresentationOrder).map(hazardClass => hazardClass.label);
    }, [hazardClassListAreDefined, hazardClassListData]);

    const registerHazardsForPresentation = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return [];
        return [...hazardsFromRegister]
            .sort((a, b) => compareHazardsByRiskLevel(a, b))
            .sort((a, b) => sortPlanRegisterHazardClassPresentationOrder(a, b, referenceHazardClassPresentationOrder));
    }, [hazardClassListAreDefined, hazardsFromRegister, referenceHazardClassPresentationOrder]);

    const hazardClassGroups = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return {};
        return Object.groupBy(registerHazardsForPresentation, (hazard) => hazard.hazardClass.label);
    }, [registerHazardsForPresentation, hazardClassListAreDefined]);

    const registerHazardGroups = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return [];
        return Object.keys(hazardClassGroups);
    }, [hazardClassGroups, hazardClassListAreDefined]);

    // Get existing query params and merge with new ones
    const currentParams = Object.fromEntries(searchParams.entries());


    return (
        <Box>
            <Box
                display="flex" flexDirection="row" alignItems="center"
                px="5px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'workbrief'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`1. Workbrief`}
                </Typography>
            </Box>
            {
                objectives.length > 0 &&
                <Box
                    display="flex" flexDirection="row" rowGap="5px"
                    my="10px"
                    flexWrap="wrap" >
                    {
                        objectives.map((objective, idx) => {
                            return (
                                <Box key={`objective-${idx}`}>
                                    <GeneralChip
                                        label={objective}
                                        backgroundColor={theme.palette.secondary.main}
                                        color={theme.palette.secondary.contrastText}
                                        size="vsmall"
                                    />
                                </Box>
                            )
                        })
                    }
                </Box>
            }
            <Box
                display="flex" flexDirection="column" justifyContent="center"
                my="10px"
            >
                <Typography
                    color={theme.palette.secondary.main}
                    fontWeight={700}
                    fontSize="0.7rem"
                >
                    Description
                </Typography>
                <Typography
                    color={theme.palette.primary.contrastText}
                    fontWeight={300}
                    fontSize="0.75rem"
                >
                    {`${prepNullableFieldToString(description) !== '' ? prepNullableFieldToString(description) : 'No description'}`}
                </Typography>
            </Box>
            <Box
                my="10px"
            >
                <Divider />
            </Box>
            <Box
                display="flex" flexDirection="column" justifyContent="center"
            >
                <Typography
                    color={theme.palette.secondary.main}
                    fontSize="0.7rem"
                    fontWeight={700}
                >
                    Other relevant information
                </Typography>
                <Typography
                    color={theme.palette.primary.contrastText}
                    fontSize="0.75rem"
                    fontWeight={300}
                >{`${prepNullableFieldToString(supportingContext) !== '' ? prepNullableFieldToString(supportingContext) : 'No other relevant information'}`}</Typography>
            </Box>
            <Box
                my="10px"
            >
                <Divider />
            </Box>
            {/* Who is involved in the work Section */}
            <Box
                display="flex" flexDirection="column" justifyContent="center"
                my="10px"
            >
                <Typography
                    color={theme.palette.secondary.main}
                    fontSize="0.7rem"
                    fontWeight={700}
                >
                    Who is involved in the work?
                </Typography>
                {(() => {
                    const allTeamMembers = [...teamMembers];

                    if (allTeamMembers.length === 0) {
                        return (
                            <Typography
                                color={theme.palette.primary.contrastText}
                                fontSize="0.75rem"
                                fontWeight={300}
                            >
                                No team members selected
                            </Typography>
                        );
                    }

                    return (
                        <Box
                            display="flex" flexDirection="row" flexWrap="wrap"
                            gap="8px"
                            mt="4px"
                        >
                            {/* Lead contact first if exists */}
                            {baseDefinedAndNotNullNarrowing(editPlanState.ourContact) && (
                                <GeneralChip
                                    key={`lead-contact-${editPlanState.ourContact}`}
                                    label={editPlanState.ourContact}
                                    backgroundColor={prodBase.secondary.main}
                                    color={prodBase.secondary.contrastText}
                                    size="vsmall"
                                />
                            )}
                            {/* Other team members */}
                            {allTeamMembers
                                .filter(member => member !== editPlanState.ourContact)
                                .map((member, idx) => (
                                    <GeneralChip
                                        key={`team-member-${idx}`}
                                        label={member}
                                        backgroundColor={prodBase.primary.main}
                                        color={prodBase.primary.contrastText}
                                        size="vsmall"
                                    />
                                ))}
                        </Box>
                    );
                })()}
            </Box>

            <Box
                display="flex" flexDirection="column" justifyContent="center"
            >
                <Typography
                    color={theme.palette.secondary.main}
                    fontSize="0.7rem"
                    fontWeight={700}
                >
                    When is the work?
                </Typography>
                <Typography
                    color={theme.palette.primary.contrastText}
                    fontSize="0.75rem"
                    fontWeight={300}
                >{`${DateTime.fromISO(startDate).toFormat('dd/MM/yyyy')} - ${DateTime.fromISO(endDate).toFormat('dd/MM/yyyy')} ${shouldCalculateDuration ? `(${Math.round(duration!.days)} day${Math.round(duration!.days) === 1 ? '' : 's'}` : ''})`}</Typography>
            </Box>
            {/* Tests Required Section */}
            {
                testsRequired && testSchedule.rows.length > 0
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="10px"
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                Tests required:
                            </Typography>
                        </Box>
                        <Box
                            display="flex" flexDirection="row" flexWrap="wrap"
                            pl="15px"
                        >
                            {
                                testSchedule.rows.map((testItem, idx) => {
                                    return (
                                        <Box key={`test-item-${idx}`} m="5px">
                                            <Typography
                                                fontSize="0.75rem"
                                                fontWeight={300}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                <strong>{`${testItem.testQuantity ?? 0}`}</strong>&nbsp;{`${testItem.testType && testItem.testType !== '' ? pluralize(testItem.testType, testItem.testQuantity ?? 0) : ''}`}
                                            </Typography>
                                        </Box>
                                    )
                                })
                            }
                        </Box>
                    </Box>
                )
            }
            {
                (!testsRequired || testSchedule.rows.length === 0)
                && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="10px" gap={1}
                        border={`1px solid ${muiAmber['600']}`}
                        borderRadius="4px"
                        p="8px"
                        bgcolor={theme.palette.background.paper}
                    >
                        <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                        <Typography
                            fontSize={"0.75rem"} color={theme.palette.primary.contrastText}
                        >
                            No specific testing is included
                        </Typography>
                    </Box>
                )
            }

            {/* Installations Required Section */}
            {
                installationsRequired
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="10px"
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                Installations required:
                            </Typography>
                        </Box>
                        <Box
                            display="flex" flexDirection="row" flexWrap="wrap"
                            pl="15px"
                        >
                            {
                                installationSchedule.rows.map((installationItem, idx) => {
                                    return (
                                        <Box key={`installation-item-${idx}`} m="5px">
                                            <Typography
                                                fontSize="0.75rem"
                                                fontWeight={300}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                <strong>{`${installationItem.installationQuantity ?? 0}`}</strong>&nbsp;{`${installationItem.installationType && installationItem.installationType !== '' ? pluralize(installationItem.installationType, installationItem.installationQuantity ?? 0) : ''}`}
                                            </Typography>
                                        </Box>
                                    )
                                })
                            }
                        </Box>
                    </Box>
                )
            }
            {
                (!installationsRequired || installationSchedule.rows.length === 0)
                && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="10px" gap={1}
                        border={`1px solid ${muiAmber['600']}`}
                        borderRadius="4px"
                        p="8px"
                        bgcolor={theme.palette.background.paper}
                    >
                        <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                        <Typography
                            fontSize={"0.75rem"} color={theme.palette.primary.contrastText}
                        >
                            No specific installations are included
                        </Typography>
                    </Box>
                )
            }
            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px" px="5px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'services'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`2. Services`}
                </Typography>
            </Box>

            {/* Site Investigation Lead Step Status - Only show here when SOP is NOT active */}
            {!serviceClearanceRequired && (
                <Box
                    display="flex" flexDirection="column"
                    gap={1}
                    border={`1px solid ${baseDefinedAndNotNullNarrowing(editPlanState.ourContact) ? muiGreen['500'] : muiAmber['600']}`}
                    borderRadius="4px"
                    p="8px"
                    bgcolor={theme.palette.background.paper}
                    my="10px"
                >
                    <Box
                        display="flex" flexDirection="row"
                        gap={1} alignItems="center"
                    >
                        {baseDefinedAndNotNullNarrowing(editPlanState.ourContact) ? (
                            <DoneIcon sx={{ color: muiGreen['500'], flexShrink: 0, fontSize: '1rem' }} />
                        ) : (
                            <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                        )}
                        <Typography
                            fontSize="0.75rem"
                            color={theme.palette.primary.contrastText}
                            fontWeight={500}
                        >
                            {baseDefinedAndNotNullNarrowing(editPlanState.ourContact)
                                ? "Initia Lead selected"
                                : "Initia Lead required"}
                        </Typography>
                        {/* Show selected contact if exists */}
                        {baseDefinedAndNotNullNarrowing(editPlanState.ourContact) && (
                            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                                <GeneralChip
                                    label={editPlanState.ourContact}
                                    backgroundColor={muiGreen['500']}
                                    color={prodBase.secondary.contrastText}
                                    size="vsmall"
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {
                serviceClearanceRequired
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="8px"
                        gap="8px"
                    >
                        {/* SOP Header */}
                        <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            border={`1px solid ${theme.palette.secondary.main}`}
                            borderRadius="4px"
                            p="8px"
                            bgcolor={theme.palette.background.paper}
                        >
                            <InfoIcon
                                sx={{
                                    color: theme.palette.secondary.main,
                                    fontSize: '1rem',
                                    mr: 1,
                                    flexShrink: 0
                                }}
                            />
                            <Typography
                                fontSize="0.7rem"
                                color={theme.palette.secondary.main}
                                fontWeight={600}
                            >
                                Initia's Service Clearance Standard Operating Procedure (SOP) applies
                            </Typography>
                        </Box>

                        {/* Site Investigation Lead Step Status - Show here when SOP is active */}
                        <Box
                            display="flex" flexDirection="column"
                            gap={1}
                            border={`1px solid ${baseDefinedAndNotNullNarrowing(editPlanState.ourContact) ? muiGreen['500'] : pink['500']}`}
                            borderRadius="4px"
                            p="8px"
                            bgcolor={theme.palette.background.paper}
                        >
                            <Box
                                display="flex" flexDirection="row"
                                gap={1} alignItems="center"
                            >
                                {baseDefinedAndNotNullNarrowing(editPlanState.ourContact) ? (
                                    <DoneIcon sx={{ color: muiGreen['500'], flexShrink: 0, fontSize: '1rem' }} />
                                ) : (
                                    <ReportIcon sx={{ color: pink['500'], flexShrink: 0, fontSize: '1rem' }} />
                                )}
                                <Typography
                                    fontSize="0.75rem"
                                    color={theme.palette.primary.contrastText}
                                    fontWeight={500}
                                >
                                    {baseDefinedAndNotNullNarrowing(editPlanState.ourContact)
                                        ? "Site Investigation Lead selected"
                                        : "Site Investigation Lead required"}
                                </Typography>
                                {/* Show selected contact if exists */}
                                {baseDefinedAndNotNullNarrowing(editPlanState.ourContact) && (
                                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                                        <GeneralChip
                                            label={editPlanState.ourContact}
                                            backgroundColor={muiGreen['500']}
                                            color={prodBase.secondary.contrastText}
                                            size="vsmall"
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Ground Clearance Checklist Status */}
                        <Box
                            display="flex" flexDirection="column"
                            gap={1}
                            border={`1px solid ${hasGroundClearanceChecklist ? muiGreen['500'] : muiAmber['600']}`}
                            borderRadius="4px"
                            p="8px"
                            bgcolor={theme.palette.background.paper}
                        >
                            <Box
                                display="flex" flexDirection="row"
                                gap={1} alignItems="center"
                            >
                                {hasGroundClearanceChecklist ? (
                                    <DoneIcon sx={{ color: muiGreen['500'], flexShrink: 0, fontSize: '1rem' }} />
                                ) : (
                                    <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                                )}
                                <Typography
                                    fontSize="0.75rem"
                                    color={theme.palette.primary.contrastText}
                                    fontWeight={500}
                                >
                                    {hasGroundClearanceChecklist
                                        ? "Ground clearance checklist completed"
                                        : "Ground clearance checklist required (or director approval needed)"}
                                </Typography>
                            </Box>

                            {/* Show uploaded ground clearance checklist file if exists */}
                            {hasGroundClearanceChecklist && (
                                <Box
                                    display="flex" flexDirection="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    ml="20px"
                                    sx={{
                                        cursor: "pointer",
                                        border: '1px solid transparent',
                                        "&:hover": { border: "1px solid lightgrey" },
                                        '&:hover .download-icon': {
                                            opacity: 1
                                        }
                                    }}
                                    onClick={async () => {
                                        const file = fileUploads.find(f =>
                                            f.name.includes(STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST) && f.includeInAppendix
                                        );
                                        if (isAuthenticated && baseDefinedAndNotNullNarrowing(file?.path)) {
                                            const response = await getS3Object(authDetail, { path: file.path });
                                            if (response?.buffer) {
                                                try {
                                                    const blob = new Blob([response.buffer], { type: response.mimeType ?? 'application/octet-stream' });
                                                    const url = window.URL.createObjectURL(blob);
                                                    const link = document.createElement('a');
                                                    link.href = url;
                                                    link.download = file.name;
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    queueSnackbar(`File saved - check your downloads`, 'success', 'Success', 3000, '1rem');
                                                    document.body.removeChild(link);
                                                    window.URL.revokeObjectURL(url);
                                                } catch (error) {
                                                    console.error('Error creating object URL:', error);
                                                    queueSnackbar(`Error getting: ${file.name}`, 'error', 'Error', 3000, '1rem');
                                                    throw error;
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <Box
                                        display="flex" flexDirection="row" alignItems="center"
                                    >
                                        <MSFileTypeIcon
                                            extension={fileUploads.find(f =>
                                                f.name.includes(STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST) && f.includeInAppendix
                                            )?.name.split('.').pop()}
                                            size={20}
                                        />
                                        &nbsp;
                                        <Typography
                                            color={theme.palette.primary.contrastText}
                                            fontSize="0.7rem"
                                            fontWeight={300}
                                        >
                                            {fileUploads.find(f =>
                                                f.name.includes(STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST) && f.includeInAppendix
                                            )?.name}
                                        </Typography>
                                    </Box>
                                    <Box
                                        className="download-icon"
                                        sx={{
                                            opacity: 0,
                                            mx: "10px",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <DownloadIcon
                                            sx={{
                                                fontSize: '0.8rem',
                                                color: 'grey'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Investigation Plan Status */}
                        <Box
                            display="flex" flexDirection="column"
                            gap={1}
                            border={`1px solid ${hasInvestigationPlan ? muiGreen['500'] : muiAmber['600']}`}
                            borderRadius="4px"
                            p="8px"
                            bgcolor={theme.palette.background.paper}
                        >
                            <Box
                                display="flex" flexDirection="row"
                                gap={1} alignItems="center"
                            >
                                {hasInvestigationPlan ? (
                                    <DoneIcon sx={{ color: muiGreen['500'], flexShrink: 0, fontSize: '1rem' }} />
                                ) : (
                                    <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                                )}
                                <Typography
                                    fontSize="0.75rem"
                                    color={theme.palette.primary.contrastText}
                                    fontWeight={500}
                                >
                                    {hasInvestigationPlan
                                        ? "Site investigation plan uploaded"
                                        : "Site investigation plan required (or director approval needed)"}
                                </Typography>
                            </Box>

                            {/* Show uploaded site investigation plan file if exists */}
                            {hasInvestigationPlan && (
                                <Box
                                    display="flex" flexDirection="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    ml="20px"
                                    sx={{
                                        cursor: "pointer",
                                        border: '1px solid transparent',
                                        "&:hover": { border: "1px solid lightgrey" },
                                        '&:hover .download-icon': {
                                            opacity: 1
                                        }
                                    }}
                                    onClick={async () => {
                                        const file = fileUploads.find(f =>
                                            f.name.includes(STANDARD_FILENAMES.INVESTIGATION_PLAN) && f.includeInAppendix
                                        );
                                        if (isAuthenticated && baseDefinedAndNotNullNarrowing(file?.path)) {
                                            const response = await getS3Object(authDetail, { path: file.path });
                                            if (response?.buffer) {
                                                try {
                                                    const blob = new Blob([response.buffer], { type: response.mimeType ?? 'application/octet-stream' });
                                                    const url = window.URL.createObjectURL(blob);
                                                    const link = document.createElement('a');
                                                    link.href = url;
                                                    link.download = file.name;
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    queueSnackbar(`File saved - check your downloads`, 'success', 'Success', 3000, '1rem');
                                                    document.body.removeChild(link);
                                                    window.URL.revokeObjectURL(url);
                                                } catch (error) {
                                                    console.error('Error creating object URL:', error);
                                                    queueSnackbar(`Error getting: ${file.name}`, 'error', 'Error', 3000, '1rem');
                                                    throw error;
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <Box
                                        display="flex" flexDirection="row" alignItems="center"
                                    >
                                        <MSFileTypeIcon
                                            extension={fileUploads.find(f =>
                                                f.name.includes(STANDARD_FILENAMES.INVESTIGATION_PLAN) && f.includeInAppendix
                                            )?.name.split('.').pop()}
                                            size={20}
                                        />
                                        &nbsp;
                                        <Typography
                                            color={theme.palette.primary.contrastText}
                                            fontSize="0.7rem"
                                            fontWeight={300}
                                        >
                                            {fileUploads.find(f =>
                                                f.name.includes(STANDARD_FILENAMES.INVESTIGATION_PLAN) && f.includeInAppendix
                                            )?.name}
                                        </Typography>
                                    </Box>
                                    <Box
                                        className="download-icon"
                                        sx={{
                                            opacity: 0,
                                            mx: "10px",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <DownloadIcon
                                            sx={{
                                                fontSize: '0.8rem',
                                                color: 'grey'
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Service Clearance Booking Status */}
                        <Box
                            display="flex" flexDirection="row"
                            gap={1} alignItems="center"
                            border={`1px solid ${serviceClearanceBooked ? muiGreen['500'] : muiAmber['600']}`}
                            borderRadius="4px"
                            p="8px"
                            bgcolor={theme.palette.background.paper}
                        >
                            {serviceClearanceBooked ? (
                                <DoneIcon sx={{ color: muiGreen['500'], flexShrink: 0, fontSize: '1rem' }} />
                            ) : (
                                <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                            )}
                            <Typography
                                fontSize="0.75rem"
                                color={theme.palette.primary.contrastText}
                                fontWeight={500}
                            >
                                {serviceClearanceBooked
                                    ? "Service clearance booking confirmed"
                                    : "Service clearance booking required (or director approval needed)"}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
            {
                !serviceClearanceRequired
                && (
                    <Box
                        display="flex" flexDirection="row"
                        gap={1} alignItems="center"
                        border={`1px solid ${muiAmber['600']}`}
                        borderRadius="4px"
                        p="8px"
                        bgcolor={theme.palette.background.paper}
                    >
                        <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                        <Typography
                            fontSize="0.75rem"
                            color={theme.palette.primary.contrastText}
                        >
                            Our activities are not expected to disturb the ground or overhead space.
                        </Typography>
                    </Box>
                )
            }


            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px" px="5px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'contactsAndBookings'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`3. Contacts and Bookings`}
                </Typography>
            </Box>

            {/* Bookings Section */}
            {
                bookings.rows.length > 0
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="10px"
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                Bookings:
                            </Typography>
                        </Box>
                        <Box
                            display="flex" flexDirection="row" flexWrap="wrap"
                            pl="15px"
                        >
                            {
                                bookings.rows.map((bookingItem, idx) => {
                                    return (
                                        <Box key={`booking-item-${idx}`} m="5px">
                                            <Typography
                                                fontSize="0.75rem"
                                                fontWeight={300}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                <strong>{`${bookingItem.contractorOrSubcontractor}`}</strong>&nbsp;{`(${bookingItem.bookingType})`}
                                            </Typography>
                                            <Typography
                                                fontSize="0.7rem"
                                                fontWeight={300}
                                                color={theme.palette.primary.contrastText}
                                            >
                                                {`${prepNullableFieldToString(bookingItem.bookingNotes)}`}
                                            </Typography>
                                        </Box>
                                    )
                                })
                            }
                        </Box>
                    </Box>
                )
            }
            {
                bookings.rows.length === 0
                && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="10px" gap={1}
                        border={`1px solid ${muiAmber['600']}`}
                        borderRadius="4px"
                        p="8px"
                        bgcolor={theme.palette.background.paper}
                    >
                        <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                        <Typography
                            fontSize={"0.75rem"} color={theme.palette.primary.contrastText}
                        >
                            No bookings have been recorded
                        </Typography>
                    </Box>
                )
            }

            {/* Site Contact Section */}
            {/* Site Contact Status */}
            <Box
                display="flex" flexDirection="column"
                gap={1}
                border={`1px solid ${baseDefinedAndNotNullNarrowing(editPlanState.siteContactId) ? muiGreen['500'] : muiAmber['600']}`}
                borderRadius="4px"
                p="8px"
                bgcolor={theme.palette.background.paper}
                my="10px"
            >
                <Box
                    display="flex" flexDirection="row"
                    gap={1} alignItems="center"
                >
                    {baseDefinedAndNotNullNarrowing(editPlanState.siteContactId) ? (
                        <DoneIcon sx={{ color: muiGreen['500'], flexShrink: 0, fontSize: '1rem' }} />
                    ) : (
                        <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                    )}
                    <Typography
                        fontSize="0.75rem"
                        color={theme.palette.primary.contrastText}
                        fontWeight={500}
                    >
                        {baseDefinedAndNotNullNarrowing(editPlanState.siteContactId)
                            ? "Site contact selected"
                            : "Site contact not selected"}
                    </Typography>

                    {/* Show selected site contact if exists */}
                    {baseDefinedAndNotNullNarrowing(editPlanState.siteContactId) && (
                        <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                            {(() => {
                                const siteContact = editPlanState.contacts.rows.find(contact => contact.id === editPlanState.siteContactId);
                                return siteContact ? (
                                    <GeneralChip
                                        label={siteContact.contactName}
                                        backgroundColor={prodBase.secondary.main}
                                        color={prodBase.secondary.contrastText}
                                        size="vsmall"
                                    />
                                ) : (
                                    <Typography
                                        color={theme.palette.primary.contrastText}
                                        fontSize="0.75rem"
                                        fontWeight={300}
                                    >
                                        Site contact not found
                                    </Typography>
                                );
                            })()}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Other Contacts Section */}
            <Box
                display="flex" flexDirection="column" justifyContent="center"
                my="10px"
            >
                <Typography
                    color={theme.palette.secondary.main}
                    fontSize="0.7rem"
                    fontWeight={700}
                >
                    Other Contacts
                </Typography>
                {(() => {
                    // Filter out the site contact to show only other contacts
                    const otherContacts = editPlanState.contacts.rows.filter(contact =>
                        contact.id !== editPlanState.siteContactId
                    );

                    if (otherContacts.length === 0) {
                        return (
                            <Typography
                                color={theme.palette.primary.contrastText}
                                fontSize="0.75rem"
                                fontWeight={300}
                            >
                                No other contacts recorded
                            </Typography>
                        );
                    }

                    return (
                        <Box
                            display="flex" flexDirection="row" flexWrap="wrap"
                            gap="8px"
                        >
                            {otherContacts.map((contact, idx) => (
                                <GeneralChip
                                    key={`other-contact-${idx}`}
                                    label={contact.contactName || contact.contactOrg || 'Unnamed Contact'}
                                    backgroundColor={theme.palette.primary.main}
                                    color={theme.palette.primary.contrastText}
                                    size="vsmall"
                                />
                            ))}
                        </Box>
                    );
                })()}
            </Box>
            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px" px="5px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'sitePlansAndPermits'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`4. Other Files`}
                </Typography>
            </Box>
            {
                fileUploads.filter(fileUpload =>
                    !fileUpload.name.includes(STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST) &&
                    !fileUpload.name.includes(STANDARD_FILENAMES.INVESTIGATION_PLAN)
                ).length === 0
                && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="10px" gap={1}
                        border={`1px solid ${muiAmber['600']}`}
                        borderRadius="4px"
                        p="8px"
                        bgcolor={theme.palette.background.paper}
                    >
                        <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                        <Typography
                            fontSize={"0.75rem"} color={theme.palette.primary.contrastText}
                        >
                            No additional file uploads
                        </Typography>
                    </Box>
                )
            }
            {
                fileUploads.filter(fileUpload =>
                    !fileUpload.name.includes(STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST) &&
                    !fileUpload.name.includes(STANDARD_FILENAMES.INVESTIGATION_PLAN)
                ).length > 0 && (
                    <Box
                        display="flex" flexDirection="column"
                        my="10px"
                        p="4px"
                        sx={{
                            containerType: 'inline-size',
                            containerName: 'file-uploads-plan-summary',
                            width: '100%'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gap: '10px',
                                '@container file-uploads-plan-summary (min-width: 550px)': {
                                    gridTemplateColumns: 'repeat(2, 1fr)'
                                },
                                '@container file-uploads-plan-summary (max-width: 549px)': {
                                    gridTemplateColumns: '1fr'
                                }
                            }}
                        >
                            {fileUploads
                                .filter(fileUpload =>
                                    !fileUpload.name.includes(STANDARD_FILENAMES.GROUND_CLEARANCE_CHECKLIST) &&
                                    !fileUpload.name.includes(STANDARD_FILENAMES.INVESTIGATION_PLAN)
                                )
                                .map((fileUpload, idx) => {
                                    return (
                                        <Box
                                            key={`plan-summary-file-upload-${idx}`}
                                            display="flex" flexDirection="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{
                                                cursor: "pointer",
                                                border: '1px solid transparent',
                                                "&:hover": { border: "1px solid lightgrey" },
                                                '&:hover .download-icon': {
                                                    opacity: 1
                                                }
                                            }}
                                            onClick={async () => {
                                                // --------------------------------
                                                if (isAuthenticated && baseDefinedAndNotNullNarrowing(fileUpload.path)) {
                                                    const response = await getS3Object(authDetail, { path: fileUpload.path });
                                                    // ---------------------
                                                    if (response?.buffer) {
                                                        try {
                                                            // ---------------------
                                                            const blob = new Blob([response.buffer], { type: response.mimeType ?? 'application/octet-stream' });
                                                            // ---------------------
                                                            // Create URL for the blob
                                                            const url = window.URL.createObjectURL(blob);
                                                            // ---------------------
                                                            // Create temporary link element
                                                            const link = document.createElement('a');
                                                            link.href = url;
                                                            link.download = fileUpload.name;
                                                            // ---------------------
                                                            // Trigger download
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            queueSnackbar(`File saved - check your downloads`, 'success', 'Success', 3000, '1rem');
                                                            // ---------------------
                                                            // Cleanup
                                                            document.body.removeChild(link);
                                                            window.URL.revokeObjectURL(url);
                                                        } catch (error) {
                                                            console.error('Error creating object URL:', error);
                                                            queueSnackbar(`Error getting: ${fileUpload.name}`, 'error', 'Error', 3000, '1rem');
                                                            throw error;
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            <Box
                                                key={`action-a-plan-file-upload-${idx}`}
                                                display="flex" flexDirection="column"
                                                flexWrap="wrap"
                                            >
                                                <Box
                                                    display="flex" flexDirection="row" alignItems="center"
                                                >
                                                    <MSFileTypeIcon
                                                        extension={fileUpload.name.split('.').pop()}
                                                        size={24}
                                                    />
                                                    &nbsp;
                                                    <Typography
                                                        color={theme.palette.primary.contrastText}
                                                        fontSize="0.75rem"
                                                        fontWeight={300}
                                                    >
                                                        {fileUpload.name}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    display="flex" flexDirection="row" alignItems="center"
                                                    pl="30px"
                                                >
                                                    <Typography
                                                        color={theme.palette.primary.contrastText}
                                                        fontSize={"0.6rem"}
                                                        fontWeight={300}
                                                    >
                                                        {fileUpload.notes}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box
                                                className="download-icon"
                                                sx={{
                                                    opacity: 0,
                                                    mx: "10px",
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <DownloadIcon
                                                    sx={{
                                                        fontSize: '1rem',
                                                        color: 'grey'
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    )
                                })}
                        </Box>
                    </Box>
                )
            }
            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px" px="5px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'hazardAssessment'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`5. Hazard Assessment`}
                </Typography>
            </Box>
            {
                criticalHazards.length === 0 && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="5px" gap={1}
                        border={`1px solid ${muiAmber['600']}`}
                        borderRadius="4px"
                        p="8px"
                        bgcolor={theme.palette.background.paper}
                    >
                        <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                        <Typography
                            fontSize={"0.75rem"} color={theme.palette.primary.contrastText}
                        >
                            No highlighted or site specific hazards recorded
                        </Typography>
                    </Box>
                )
            }
            {
                criticalHazards.length > 0
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="5px"
                        p="4px"
                        sx={{
                            containerType: 'inline-size',
                            containerName: `critical-hazard-plan-summary`,
                            width: '100%'
                        }}
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                            pb="5px"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                {`Highlighted or site specific hazards (${criticalHazards.length})`}
                            </Typography>
                        </Box>
                        {
                            criticalHazards.length > 0 &&
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: '10px',
                                    '@container critical-hazard-plan-summary (min-width: 700px)': {
                                        gridTemplateColumns: 'repeat(2, 1fr)'
                                    },
                                    '@container critical-hazard-plan-summary (max-width: 699px)': {
                                        gridTemplateColumns: '1fr'
                                    }
                                }}
                            >
                                {criticalHazards.map(criticalHazard => {
                                    // --------------------------
                                    return (
                                        <CriticalOrSiteSpecificHazardListItemReadOnly
                                            key={criticalHazard.id}
                                            hazard={criticalHazard}
                                            actionButtons={[
                                                <IconButton
                                                    size="small"
                                                    tooltipTitle="Delete hazard"
                                                    onClick={(event) => {
                                                        // ---------------------
                                                        event.stopPropagation();
                                                        updateEditPlanState({
                                                            ...editPlanState,
                                                            criticalHazards: editPlanState.criticalHazards.filter(hazard => hazard.id !== criticalHazard.id)
                                                        });
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            ]}
                                        />
                                    )
                                })}
                            </Box>
                        }
                    </Box>
                )
            }
            {
                hazardsFromRegister.length === 0 && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="5px" gap={1}
                        border={`1px solid ${pink['500']}`}
                        borderRadius="4px"
                        p="8px"
                        bgcolor={theme.palette.background.paper}
                    >
                        <ReportIcon sx={{ color: pink['500'], flexShrink: 0, fontSize: '1rem' }} />
                        <Typography
                            fontSize={"0.75rem"} color={theme.palette.primary.contrastText}
                        >
                            No register hazards recorded
                        </Typography>
                    </Box>
                )
            }
            {
                hazardsFromRegister.length > 0
                && (
                    <Box
                        display="flex" flexDirection="column"
                        my="5px"
                        p="4px"
                        sx={{
                            containerType: 'inline-size',
                            containerName: `register-hazard-plan-summary`,
                            width: '100%'
                        }}
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                            pb="5px"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                {`Register hazard groups (${registerHazardGroups.length} group${registerHazardGroups.length === 1 ? '' : 's'}, ${hazardsFromRegister.length} hazard${hazardsFromRegister.length === 1 ? '' : 's'})`}
                            </Typography>
                        </Box>
                        {
                            hazardsFromRegister.length > 0 &&
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: '10px',
                                    '@container register-hazard-plan-summary (min-width: 700px)': {
                                        gridTemplateColumns: 'repeat(2, 1fr)'
                                    },
                                    '@container register-hazard-plan-summary (max-width: 699px)': {
                                        gridTemplateColumns: '1fr'
                                    }
                                }}
                            >
                                {
                                    registerHazardGroups.map(hazardClassLabel => {
                                        // ---------------------
                                        return (
                                            <PlanRegisterHazardClassListItemReadOnly
                                                key={`plan-summary-register-hazard-group-${hazardClassLabel}`}
                                                hazardClassLabel={hazardClassLabel}
                                                hazards={hazardClassGroups[hazardClassLabel] ?? []}
                                                actionButtons={[
                                                    <IconButton
                                                        size="small"
                                                        tooltipTitle="Delete hazard"
                                                        onClick={(event) => {
                                                            // ---------------------
                                                            event.stopPropagation();
                                                            updateEditPlanState({
                                                                ...editPlanState,
                                                                hazardsFromRegister: editPlanState.hazardsFromRegister.filter(hazard => hazard.hazardClass.label !== hazardClassLabel)
                                                            });
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                ]}
                                            />
                                        )
                                    })
                                }
                            </Box>
                        }
                    </Box>
                )
            }
            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px" px="5px"
                sx={{ cursor: "pointer", border: '1px solid transparent', "&:hover": { border: "1px solid lightgrey" } }}
                onClick={() => navigate({
                    search: new URLSearchParams({
                        ...currentParams,
                        stage: 'ppeAndTools'
                    }).toString()
                })}
            >
                <Typography
                    fontSize="0.9rem"
                    fontWeight={700}
                    color={theme.palette.primary.contrastText}
                >
                    {`6. PPE and Tools`}
                </Typography>
            </Box>
            {
                equipment.length === 0 && (
                    <Box
                        display="flex" flexDirection="row" alignItems="center"
                        my="5px" gap={1}
                        border={`1px solid ${muiAmber['600']}`}
                        borderRadius="4px"
                        p="8px"
                        bgcolor={theme.palette.background.paper}
                    >
                        <ReportIcon sx={{ color: muiAmber['600'], flexShrink: 0, fontSize: '1rem' }} />
                        <Typography
                            fontSize={"0.75rem"} color={theme.palette.primary.contrastText}
                        >
                            No PPE and tools recorded
                        </Typography>
                    </Box>
                )
            }
            {
                equipment.length > 0 && (
                    <Box
                        display="flex" flexDirection="column"
                        my="5px"
                        p="4px"
                        sx={{
                            containerType: 'inline-size',
                            containerName: `equipment-plan-summary`,
                            width: '100%'
                        }}
                    >
                        <Box
                            display="flex" flexDirection="row" gap={1} alignItems="center"
                            pb="5px"
                        >
                            <Typography
                                color={theme.palette.secondary.main}
                                fontSize="0.7rem"
                                fontWeight={700}
                            >
                                {`PPE and tools (${equipment.length})`}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'grid',
                                gap: '10px',
                                '@container equipment-plan-summary (min-width: 700px)': {
                                    gridTemplateColumns: 'repeat(3, 1fr)'
                                },
                                '@container equipment-plan-summary (max-width: 699px)': {
                                    gridTemplateColumns: 'repeat(2, 1fr)'
                                },
                                '@container equipment-plan-summary (max-width: 399px)': {
                                    gridTemplateColumns: '1fr'
                                }
                            }}
                        >
                            {equipment.map((item: IEquipmentClassUse, idx) => (
                                <Box
                                    key={`equipment-item-${idx}`}
                                    display="flex"
                                    flexDirection="column"
                                    p="2px"
                                >
                                    <Typography
                                        color={theme.palette.primary.contrastText}
                                        fontSize="0.75rem"
                                        fontWeight={300}
                                    >
                                        {formatDisplayString(item.name)}
                                    </Typography>
                                    {baseDefinedAndNotNullNarrowing(item.notes) && (
                                        <Typography
                                            color={theme.palette.primary.contrastText}
                                            fontSize="0.5rem"
                                            fontWeight={300}
                                        >
                                            {formatDisplayString(item.notes)}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )
            }
        </Box>
    );
};